import React, { Component, createRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import { searchBusinesses } from '../../util/api';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  initialValues,
  validUrlQueryParamsFromProps,
  getDerivedRenderData,
  onResetAll,
  createFilterValueChangeHandler,
  onSortBy,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      businesses: [],
      businessesLoading: false,
      businessesError: false,
      businessCanScrollLeft: false,
      businessCanScrollRight: false,
    };

    this.businessCarouselRef = createRef();
    this.updateBusinessCarousel = this.updateBusinessCarousel.bind(this);
    this.scrollBusinessCarousel = this.scrollBusinessCarousel.bind(this);

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  componentDidMount() {
    this.businessSearchMounted = true;
    this.loadBusinesses();
  }

  componentDidUpdate(prevProps) {
    const previousKeywords = parse(prevProps.location.search).keywords || '';
    const currentKeywords = parse(this.props.location.search).keywords || '';

    if (previousKeywords !== currentKeywords) {
      this.loadBusinesses();
    }
  }

  componentWillUnmount() {
    this.businessSearchMounted = false;
    this.businessSearchRequestId = (this.businessSearchRequestId || 0) + 1;
  }

  loadBusinesses() {
    const keywords = parse(this.props.location.search).keywords || '';
    const requestId = (this.businessSearchRequestId || 0) + 1;
    this.businessSearchRequestId = requestId;

    this.setState({
      businesses: [],
      businessesLoading: true,
      businessesError: false,
    });

    searchBusinesses(keywords, 1)
      .then(result => {
        if (!this.businessSearchMounted || this.businessSearchRequestId !== requestId) {
          return;
        }

        this.setState({
          businesses: result.businesses || [],
          businessesLoading: false,
          businessesError: false,
          businessCanScrollLeft: false,
          businessCanScrollRight: false,
        }, () => this.updateBusinessCarousel());
      })
      .catch(() => {
        if (!this.businessSearchMounted || this.businessSearchRequestId !== requestId) {
          return;
        }

        this.setState({
          businesses: [],
          businessesLoading: false,
          businessesError: true,
        });
      });
  }

  updateBusinessCarousel() {
    const element = this.businessCarouselRef.current;
    if (!element) return;

    const canScrollLeft = element.scrollLeft > 2;
    const canScrollRight =
      element.scrollLeft + element.clientWidth < element.scrollWidth - 2;

    if (
      canScrollLeft !== this.state.businessCanScrollLeft ||
      canScrollRight !== this.state.businessCanScrollRight
    ) {
      this.setState({
        businessCanScrollLeft: canScrollLeft,
        businessCanScrollRight: canScrollRight,
      });
    }
  }

  scrollBusinessCarousel(direction) {
    const element = this.businessCarouselRef.current;
    if (!element) return;

    const card = element.querySelector('li');
    const distance = card ? card.getBoundingClientRect().width + 16 : 280;
    element.scrollBy({ left: direction * distance, behavior: 'smooth' });
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    onResetAll({
      history,
      routeConfiguration,
      config,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      setState: this.setState.bind(this),
    });
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    return createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    const {
      listingTypePathParam,
      sortConfig,
      validQueryParams,
      availableFilters,
      selectedFilters,
      isValidDatesFilter,
      selectedFiltersCountForMobile,
      totalItems,
      listingsAreLoaded,
      conflictingFilterActive,
      showCreateListingsLink,
      title,
      description,
      schema,
      marketplaceCurrency,
      listingCategories,
    } = getDerivedRenderData({
      intl,
      location,
      config,
      routeConfiguration,
      searchParams,
      pagination,
      listings,
      searchInProgress,
      currentPathParams,
      currentUser,
    });

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
        hasMatchingBusinesses={
          !this.state.businessesLoading &&
          !this.state.businessesError &&
          this.state.businesses.length > 0
        }
      />
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={css.layoutWrapperContainer}>
          <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
            <div className={css.filterColumnContent}>
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;
                const filterId = `SearchFiltersDesktop.${filterConfig.key.toLowerCase()}`;
                return (
                  <FilterComponent
                    key={key}
                    id={filterId}
                    className={css.filter}
                    config={filterConfig}
                    containerId="SearchPageWithGrid_DesktopFilters"
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(this.props, this.state.currentQueryParams)}
                    getHandleChangedValueFn={this.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                    isDesktop
                  />
                );
              })}
              <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
              </button>
            </div>
          </aside>

          <div id="main-content" className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                location={location}
              >
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;

                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      config={filterConfig}
                      containerId="SearchPage_MobileFilters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile>
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
              />
              <div
                className={classNames(css.listingsForGridVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                <SearchErrors
                  searchListingsError={searchListingsError}
                  isValidDatesFilter={isValidDatesFilter}
                />
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  isMapVariant={false}
                  listingTypeParam={listingTypePathParam}
                  intl={intl}
                />
              </div>
              <section aria-label="Local Businesses" className={css.localBusinessesSection}>
                <div className={css.localBusinessesHeader}>
                  <h2>Local Businesses</h2>
                </div>
                {this.state.businessesLoading ? (
                  <p>Searching local businesses...</p>
                ) : this.state.businessesError ? (
                  <p>Business results are temporarily unavailable.</p>
                ) : this.state.businesses.length > 0 ? (
                  <div className={css.localBusinessesCarouselWrapper}>
                    <ul
                      ref={this.businessCarouselRef}
                      className={css.localBusinessesCarousel}
                      onScroll={this.updateBusinessCarousel}
                      aria-label="Browse matching local businesses"
                      style={{ listStyle: 'none', margin: 0 }}
                    >
                      {this.state.businesses.map(business => (
                        <li key={business.id} className={css.localBusinessCard}>
                          {!business.profileImageUrl && (
                            <a
                              href={`/u/${encodeURIComponent(business.id)}`}
                              style={{
                                display: 'flex',
                                height: '160px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#e8f3eb',
                                borderRadius: '8px',
                                marginBottom: '12px',
                                color: '#16803c',
                                fontSize: '42px',
                                fontWeight: 700,
                                textDecoration: 'none',
                              }}
                              aria-label={`${business.businessName || business.displayName} profile`}
                            >
                              {(business.businessName || business.displayName || 'Local Business')
                                .trim()
                                .split(/\s+/)
                                .slice(0, 2)
                                .map(word => word.charAt(0))
                                .join('')
                                .toUpperCase()}
                            </a>
                          )}
                          {business.profileImageUrl && (
                            <a href={`/u/${encodeURIComponent(business.id)}`}>
                              <img
                                src={business.profileImageUrl}
                                alt={`${business.businessName || business.displayName} profile`}
                                loading="lazy"
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  height: '160px',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  marginBottom: '12px',
                                }}
                              />
                            </a>
                          )}
                          <a href={`/u/${encodeURIComponent(business.id)}`}>
                            <strong>{business.businessName || business.displayName}</strong>
                          </a>
                          {business.businessCategory && (
                            <p style={{ margin: '8px 0 0' }}>
                              {(() => {
                                const categories = config.categoryConfiguration?.categories || [];
                                const category = categories.find(
                                  item => item.id === business.businessCategory
                                );
                                const subcategory = category?.subcategories?.find(
                                  item => item.id === business.businessSubcategory
                                );
                                const categoryName =
                                  category?.name || business.businessCategory.replace(/-/g, ' ');
                                const subcategoryName = business.businessSubcategory
                                  ? subcategory?.name ||
                                    business.businessSubcategory.replace(/-/g, ' ')
                                  : null;

                                return subcategoryName
                                  ? `${categoryName} · ${subcategoryName}`
                                  : categoryName;
                              })()}
                            </p>
                          )}
                          {business.bio && <p>{business.bio}</p>}
                        </li>
                      ))}
                    </ul>
                    {this.state.businessCanScrollLeft && (
                      <button
                        type="button"
                        className={css.localBusinessArrowPrevious}
                        aria-label="Previous local businesses"
                        onClick={() => this.scrollBusinessCarousel(-1)}
                      >
                        ‹
                      </button>
                    )}
                    {this.state.businessCanScrollRight && (
                      <button
                        type="button"
                        className={css.localBusinessArrowNext}
                        aria-label="Next local businesses"
                        onClick={() => this.scrollBusinessCarousel(1)}
                      >
                        ›
                      </button>
                    )}
                  </div>
                ) : (
                  <p>No matching local businesses found.</p>
                )}
                {!this.state.businessesLoading &&
                  !this.state.businessesError &&
                  this.state.businesses.length > 0 && (
                    <p style={{ marginTop: '20px' }}>
                      <a
                        href={`/local-businesses${
                          new URLSearchParams(location.search).get('keywords')
                            ? `?${new URLSearchParams({
                                keywords: new URLSearchParams(location.search).get('keywords'),
                              }).toString()}`
                            : ''
                        }`}
                      >
                        View All Local Businesses →
                      </a>
                    </p>
                  )}
              </section>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage "container" (grid layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);

  const currentUser = useSelector(state => state.user?.currentUser);
  const { pagination, searchInProgress, searchListingsError, searchParams } = useSelector(
    state => state.SearchPage
  );
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      currentUser={currentUser}
      listings={listings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  );
};

export default SearchPage;
